/**
 * @magic-md/editor-core — editor store (composed)
 *
 * The previous monolithic file (2534 lines) has been split into focused
 * Zustand slices under `./editor/`:
 *
 *   - `cursor-slice`  — cursor position + parsed document cache
 *   - `view-slice`    — view mode, sidebar, MCP, detail panes, status
 *   - `pane-slice`    — multi-pane layout + per-pane state
 *   - `workspace-slice` — workspace folders + file tree + file ops
 *   - `tabs-slice`    — tabs, file open/save, content updates
 *
 * Each slice owns a disjoint set of state fields and actions. Cross-slice
 * calls (e.g. `workspace.deleteNode → closeTab`) flow through `get()` over
 * the composed `EditorState`, which is the simplest pattern for Zustand
 * slices that share state mutations.
 *
 * The public surface (`useEditorStore`, exported types, `getSidebarPresentation`,
 * `isTabMigrating`) is preserved exactly.
 */

import { create } from "zustand";
import {
    createCursorSlice,
    createPaneSlice,
    createTabsSlice,
    createViewSlice,
    createWorkspaceSlice,
} from "./editor";
import type { EditorState } from "./editor/types";

export type {
    EditorTab,
    FileTreeNode,
    PaneLayout,
    PaneStateEntry,
    PreviewWidth,
    SidebarPresentation,
    SidebarView,
    SnapshotPreviewState,
    ViewMode,
} from "./editor/types";
export {
    countExpandedPaneLeaves,
    getPaneLayoutLeafIds,
    getSidebarPresentation,
    isPaneCollapsedInLayout,
    isTabMigrating,
    selectEffectiveViewMode,
} from "./editor";

export const useEditorStore = create<EditorState>()((...a) => ({
    ...createCursorSlice(...a),
    ...createViewSlice(...a),
    ...createWorkspaceSlice(...a),
    ...createPaneSlice(...a),
    ...createTabsSlice(...a),
}));
